/**
 * DAS LEB-VERZEICHNIS (.523): alle erzeugten LEBs als Liste, jede mit ihrer
 * Datei, die Liste als CSV. Nur mit `leb_verzeichnis.lesen` (Administration).
 *
 * ORIGINALTEXT MARVIN (28.10.2026)
 *   „suche ich gerade die LEBs, die ich hochgeladen habe, als Liste und als
 *    Datei. Können wir das irgendwie als Menüpunkt rausziehen auf die Seite
 *    und natürlich nur für mich als Administrator zugänglich machen?"
 */
import { useEffect, useState } from 'react';
import { api, darf, datum, euro, zeitpunkt, type Anmelder } from '../api.ts';
import { Kopf, Karte } from '../teile/Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

const ZUSTAND: Record<string, string> = {
  offen: 'offen, keine Gutschrift', unklar: 'Gutschrift ohne Betrag', ohne_wert: 'LEB ohne Wert', abgerechnet: 'abgerechnet',
  zu_wenig: 'zu wenig gutgeschrieben', zu_viel: 'zu viel gutgeschrieben',
};

export default function LebVerzeichnis({ ich, oeffne }: { ich: Anmelder; oeffne: (auftragId: string, reiter?: string) => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [suche, setSuche] = useState('');
  const [monat, setMonat] = useState('');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [art, setArt] = useState('');
  const [zustand, setZustand] = useState('');
  const mitGeld = darf(ich, 'abrechnung.geld');
  const params = () => {
    const p = new URLSearchParams();
    if (suche.trim()) p.set('suche', suche.trim());
    if (monat) p.set('monat', monat); if (von) p.set('von', von); if (bis) p.set('bis', bis);
    if (art) p.set('art', art); if (zustand) p.set('zustand', zustand);
    return p;
  };
  const laden = () => api.lebVerzeichnis(params()).then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [monat, von, bis, art, zustand]);
  useFrisch(laden);
  const zeilen: any[] = d?.zeilen ?? [];
  const summe = mitGeld ? zeilen.reduce((s, z) => s + (Number(z.leb_wert) || 0), 0) : null;
  return (
    <>
      <Kopf titel="LEB-Verzeichnis" weg={d ? `${zeilen.length} LEBs${summe != null ? `, ${euro(summe)} Wert` : ''}` : 'Alle erzeugten Leistungserfassungsblätter'}
            rechts={<a className="knopf" href={'/api/leb-verzeichnis.csv?' + params().toString()} target="_blank" rel="noreferrer" title="Die Liste mit den aktuellen Filtern als CSV für Excel">CSV herunterladen</a>} />
      <Fehlerkasten lage={fehler} />
      <div className="reihe" style={{ alignItems: 'flex-end', marginBottom: 10, flexWrap: 'wrap' }}>
        <div className="feld" style={{ width: 300 }}>
          <label>Suche</label>
          <input value={suche} placeholder="KLS-ID, SM-Nummer, Belegnummer, Anschrift, Dateiname" onChange={(e) => setSuche(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') laden(); }} />
        </div>
        <div className="feld" style={{ width: 170 }}>
          <label>Monat</label>
          <select value={monat} onChange={(e) => setMonat(e.target.value)}>
            <option value="">alle</option>
            {(d?.monate ?? []).map((m: any) => <option key={m.monat} value={m.monat}>{m.monat.split('-').reverse().join('/')} ({m.n})</option>)}
          </select>
        </div>
        <div className="feld" style={{ width: 160 }}><label>Von</label><input type="date" value={von} onChange={(e) => setVon(e.target.value)} /></div>
        <div className="feld" style={{ width: 160 }}><label>Bis</label><input type="date" value={bis} onChange={(e) => setBis(e.target.value)} /></div>
        <div className="feld" style={{ width: 140 }}>
          <label>Art</label>
          <select value={art} onChange={(e) => setArt(e.target.value)}><option value="">alle</option><option value="teil">Teil-LEB</option><option value="schluss">Schluss-LEB</option></select>
        </div>
        <div className="feld" style={{ width: 190 }}>
          <label>Gutschrift</label>
          <select value={zustand} onChange={(e) => setZustand(e.target.value)}>
            <option value="">alle</option>
            {(d?.zustaende ?? []).map((z: any) => <option key={z.zustand} value={z.zustand}>{ZUSTAND[z.zustand] ?? z.zustand} ({z.n})</option>)}
          </select>
        </div>
        <button type="button" className="knopf" onClick={laden}>Suchen</button>
      </div>
      <Karte titel={`LEBs (${zeilen.length})`}>
        {d && !zeilen.length && <div className="leise klein">Keine LEB in dieser Auswahl.</div>}
        {!!zeilen.length && (
          <table className="liste">
            <thead><tr><th>Erzeugt</th><th>Auftrag</th><th>Art</th><th>Beleg</th><th>Leistungszeitraum</th><th className="zahl">Pos.</th>{mitGeld && <th className="zahl">Wert</th>}<th>Gutschrift</th><th>Datei</th></tr></thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.id} style={{ cursor: 'default' }}>
                  <td className="klein">{zeitpunkt(z.erzeugt_am)}<div className="leise">{z.erzeugt_von_name ?? z.erzeugt_von ?? ''}</div></td>
                  <td className="klein">
                    <button type="button" className="alslink" onClick={() => oeffne(z.auftrag_id, 'leb')} title="Akte auf dem Reiter LEB öffnen"><b>{z.strasse} {z.hausnummer}{z.hausnummer_zusatz ?? ''}</b></button>
                    <div className="leise">{z.plz} {z.ort_text}, KLS {z.kls_id ?? '–'}, SM {z.sm_nummer ?? '–'}</div>
                  </td>
                  <td className="klein">{z.art === 'teil' ? 'Teil' : 'Schluss'}</td>
                  <td className="klein">{z.belegnummer}<div className="leise">Pos. {z.belegposition}</div></td>
                  <td className="klein">{datum(z.lz_von)} bis {datum(z.lz_bis)}</td>
                  <td className="zahl klein">{z.positionen ?? ''}{z.ohne_preis ? <div className="leise" title="Positionen ohne Preis im Rahmenvertrag">{z.ohne_preis} ohne Preis</div> : null}</td>
                  {mitGeld && <td className="zahl klein">{z.leb_wert != null ? euro(z.leb_wert) : <span className="leise">–</span>}</td>}
                  <td className="klein">
                    <span className={'marke-zustand ' + (z.zustand === 'abgerechnet' ? 'z-erledigt' : ['zu_wenig', 'zu_viel'].includes(z.zustand) ? 'z-unterbrochen' : 'z-wartet')}>{ZUSTAND[z.zustand] ?? z.zustand}</span>
                    {z.gutschrift_nummern && <div className="leise">{z.gutschrift_nummern}{z.gutschrift_zuletzt ? `, ${datum(z.gutschrift_zuletzt)}` : ''}{mitGeld && z.gutschrift_betrag != null ? `, ${euro(z.gutschrift_betrag)}` : ''}</div>}
                  </td>
                  <td className="klein">
                    {z.datei ? <a className="knopf klein" target="_blank" rel="noreferrer" href={api.dateiAdresse(z.datei, z.dateiname)}>LEB öffnen</a> : <span className="leise" title={z.dateiname}>Datei nicht mehr abgelegt</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>
    </>
  );
}
